import React, { useState } from "react";
import { fetchWithAuth, useUser } from "../Context/UserContext";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import API_URL from "../config";
import { Plus, Pencil } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";

const inputClass =
  "w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-50";

const UserExperience = ({
  experiences = [],
  isOwner,
  updateProfileExperience,
}) => {
  const { setUser } = useUser();
  const [isModalOpen, setIsModalOpen] = useState(false);

  // experienceList manejará todas las experiencias como un array
  const [experienceList, setExperienceList] = useState(
    Array.isArray(experiences) ? experiences : []
  );

  // experienceData será para manejar la experiencia actual que se está editando o agregando
  const [experienceData, setExperienceData] = useState({
    id_experience: null,
    empresa: "",
    titulo_puesto: "",
    fecha_inicio: "",
    fecha_fin: "",
    descripcion: "",
    tipo_empleo: "",
    actualmente: false,
    en_curso: false,
  });

  const formatDate = (date) => {
    if (!date) return "";
    const d = new Date(date);
    return d.toISOString().split("T")[0]; // Formato YYYY-MM-DD
  };

  const handleSave = async () => {
    if (!isOwner) {
      console.error("No tienes permiso para modificar este perfil.");
      return;
    }

    // Validar que los campos obligatorios no estén vacíos
    if (!experienceData.titulo_puesto.trim()) {
      toast.error("El campo 'Cargo' es obligatorio.");
      return;
    }

    if (!experienceData.empresa.trim()) {
      toast.error("El campo 'Nombre de la empresa' es obligatorio.");
      return;
    }

    if (!experienceData.fecha_inicio) {
      toast.error("El campo 'Fecha de inicio' es obligatorio.");
      return;
    }

    if (!experienceData.en_curso && !experienceData.fecha_fin) {
      toast.warning(
        "Debe ingresar una fecha de finalización o marcar 'Actualmente en el cargo'."
      );
      return;
    }

    // Validar que la fecha de fin sea posterior o igual a la fecha de inicio
    if (
      !experienceData.en_curso &&
      new Date(experienceData.fecha_fin) < new Date(experienceData.fecha_inicio)
    ) {
      toast.warning(
        "La 'Fecha de finalización' debe ser posterior a la 'Fecha de inicio'."
      );
      return;
    }

    try {
      const url = `${API_URL}/profile/save_experience`;

      const response = await fetchWithAuth(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(experienceData),
      });

      if (response.ok) {
        setUser((prevUser) => ({
          ...prevUser,
          experiences: experienceData.id_experience
            ? prevUser.experiences.map((exp) =>
                exp.id_experience === experienceData.id_experience
                  ? { ...exp, ...experienceData }
                  : exp
              )
            : [experienceData, ...prevUser.experiences],
        }));

        setExperienceList((prevList) => {
          return experienceData.id_experience
            ? prevList.map((exp) =>
                exp.id_experience === experienceData.id_experience
                  ? { ...exp, ...experienceData }
                  : exp
              )
            : [experienceData, ...prevList];
        });

        setIsModalOpen(false);
      } else {
        const errorData = await response.json();
        console.error("Error al guardar la experiencia:", errorData.error);
      }
    } catch (error) {
      console.error("Error en la solicitud:", error);
    }
  };

  const handleChange = (e) => {
    setExperienceData({ ...experienceData, [e.target.name]: e.target.value });
  };

  const handleEdit = (exp) => {
    setExperienceData({
      id_experience: exp.id_experience, // 🔹 Asegurar que mantiene el ID
      empresa: exp.empresa,
      titulo_puesto: exp.titulo_puesto,
      fecha_inicio: formatDate(exp.fecha_inicio),
      fecha_fin: exp.en_curso
        ? new Date().toISOString().split("T")[0]
        : formatDate(exp.fecha_fin),
      descripcion: exp.descripcion,
      tipo_empleo: exp.tipo_empleo,
      actualmente: exp.en_curso,
      en_curso: exp.en_curso,
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id_experience) => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/profile/delete_experience/${id_experience}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        }
      );

      if (response.ok) {
        setUser((prevUser) => ({
          ...prevUser,
          experiences: prevUser.experiences.filter(
            (exp) => exp.id_experience !== id_experience
          ),
        }));
        setExperienceList((prevList) =>
          prevList.filter((exp) => exp.id_experience !== id_experience)
        );
        setIsModalOpen(false); // Cerrar el modal después de eliminar
      } else {
        console.error("Error al eliminar la experiencia.");
      }
    } catch (error) {
      console.error("Error en la solicitud:", error);
    }
  };

  const toggleActualmente = () => {
    setExperienceData((prevData) => ({
      ...prevData,
      actualmente: !prevData.actualmente,
      en_curso: !prevData.actualmente,
      fecha_fin: !prevData.actualmente ? "" : prevData.fecha_fin,
    }));
  };

  const formatDateWithDuration = (startDate, endDate, isCurrent) => {
    if (!startDate) return "";

    const start = new Date(startDate);
    const today = new Date();
    const end = isCurrent || endDate === null ? today : new Date(endDate);

    const startFormatted = format(start, "MMM. yyyy", { locale: es });
    const endFormatted =
      isCurrent || endDate === null
        ? "Actualidad"
        : format(end, "MMM. yyyy", { locale: es });

    const diffInMonths =
      end.getMonth() -
      start.getMonth() +
      12 * (end.getFullYear() - start.getFullYear());

    const years = Math.floor(diffInMonths / 12);
    const months = diffInMonths % 12;

    const duration =
      years > 0 && months > 0
        ? `${years} año${years !== 1 ? "s" : ""} y ${months} mes${
            months !== 1 ? "es" : ""
          }`
        : years > 0
        ? `${years} año${years !== 1 ? "s" : ""}`
        : `${months} mes${months !== 1 ? "es" : ""}`;

    return `${startFormatted} - ${endFormatted} · ${duration}`;
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-foreground">Experiencia:</h3>
          {isOwner && (
            <button
              onClick={() => {
                setExperienceData({
                  id_experience: null,
                  empresa: "",
                  titulo_puesto: "",
                  fecha_inicio: "",
                  fecha_fin: "",
                  descripcion: "",
                  tipo_empleo: "",
                  actualmente: false,
                  en_curso: false,
                });
                setIsModalOpen(true);
              }}
              className="text-muted-foreground hover:text-foreground"
              aria-label="Añadir experiencia"
            >
              <Plus className="h-4 w-4" />
            </button>
          )}
        </div>

        {experienceList.length === 0 ? (
          <p className="mt-2 text-sm text-muted-foreground">
            {isOwner
              ? "Registra una experiencia haciendo click al signo '+' que sale en la esquina derecha de esta sección."
              : "El usuario no ha registrado experiencia todavía."}
          </p>
        ) : (
          <div className="mt-3 space-y-4">
            {experienceList.map((exp, index) => (
              <div key={index} className="border-t border-border pt-3 first:border-t-0 first:pt-0">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-foreground">
                    {exp.titulo_puesto} - {exp.empresa}
                  </h4>
                  {isOwner && (
                    <button
                      onClick={() => handleEdit(exp)}
                      className="text-muted-foreground hover:text-foreground"
                      aria-label="Editar"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  {formatDateWithDuration(exp.fecha_inicio, exp.fecha_fin, exp.actualmente)}
                </p>
                <p
                  className="mt-1 text-sm text-muted-foreground"
                  dangerouslySetInnerHTML={{
                    __html: (exp.descripcion || "").replace(/\n/g, "<br>"),
                  }}
                />
              </div>
            ))}
          </div>
        )}

        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {experienceData.id_experience ? "Editar Experiencia" : "Agregar Experiencia"}
              </DialogTitle>
            </DialogHeader>
            <p className="text-xs text-muted-foreground">* El asterisco indica que es obligatorio</p>
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">Cargo *</label>
                <Input
                  type="text"
                  name="titulo_puesto"
                  value={experienceData.titulo_puesto}
                  onChange={handleChange}
                  placeholder="Ejemplo: Responsable de ventas"
                  required
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">
                  Nombre de la empresa *
                </label>
                <Input
                  type="text"
                  name="empresa"
                  value={experienceData.empresa}
                  onChange={handleChange}
                  placeholder="Ejemplo: Microsoft"
                  required
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">Fecha de inicio *</label>
                <Input
                  type="date"
                  name="fecha_inicio"
                  value={experienceData.fecha_inicio}
                  onChange={handleChange}
                  required
                />
              </div>
              <div>
                <div className="mb-1 flex items-center justify-between">
                  <label className="text-sm font-medium text-foreground">Fecha de finalización*</label>
                  <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    Actualmente en el cargo:
                    <input
                      type="checkbox"
                      checked={experienceData.actualmente || experienceData.en_curso}
                      onChange={toggleActualmente}
                    />
                  </label>
                </div>
                <Input
                  type="date"
                  name="fecha_fin"
                  value={experienceData.fecha_fin}
                  onChange={handleChange}
                  disabled={experienceData.actualmente || experienceData.en_curso}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">Descripción</label>
                <textarea
                  className={inputClass}
                  rows={3}
                  name="descripcion"
                  value={experienceData.descripcion}
                  onChange={handleChange}
                  placeholder="Ejemplo: Soy un ingeniero con 10 años de experiencia en desarrollo de software..."
                  maxLength="2000"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">Tipo de empleo</label>
                <Input
                  type="text"
                  name="tipo_empleo"
                  value={experienceData.tipo_empleo}
                  onChange={handleChange}
                  placeholder="Ejemplo: Tiempo completo"
                />
              </div>
            </div>
            {isOwner && (
              <DialogFooter>
                {experienceData.id_experience && (
                  <Button variant="destructive" onClick={() => handleDelete(experienceData.id_experience)}>
                    Eliminar experiencia
                  </Button>
                )}
                <Button onClick={handleSave}>Guardar</Button>
              </DialogFooter>
            )}
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default UserExperience;
